import React, { PropsWithChildren, useEffect, useState } from "react";

import { flip, FloatingPortal, offset, shift, size, useFloating } from "@floating-ui/react";
import { TagSurfaceContext } from "emoreg/atoms/Tag/context";
import { useIsFocused } from "emoreg/hooks/use-is-focused";
import { useChangeEffect } from "hassibot/hooks/use-change-effect";

import { isDropdownAnchorComp, isDropdownPopOverComp } from "./utils";

type DropdownProps = {
  initialIsOpen?: boolean;
  position?: "bottom-start" | "bottom-end";
};

export const Dropdown = ({
  initialIsOpen = false,
  children,
  position = "bottom-start",
}: PropsWithChildren<DropdownProps>) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [isFocus, focusEvents] = useIsFocused();

  // extract meaningfull children
  const chilList = React.Children.toArray(children);
  const AnchorComp = chilList.find(isDropdownAnchorComp);
  const ContentComp = chilList.find(isDropdownPopOverComp);

  // extract style from content component
  const contentStyle = ContentComp?.props.style;
  const contentObjectStyle = typeof contentStyle === "function" ? {} : contentStyle;
  const contentFunctionStyle = typeof contentStyle === "function" ? contentStyle : () => ({});

  const { refs, floatingStyles } = useFloating({
    placement: position,
    strategy: "fixed",
    open: isOpen,
    middleware: [
      offset({ mainAxis: 4 }),
      flip(),
      shift(),
      size({
        apply:
          // overload dynamic floating style based on anchor component
          ({ elements }) => {
            Object.assign(
              elements.floating.style,
              contentFunctionStyle(elements.reference as HTMLElement)
            );
          },
      }),
    ],
  });

  // if focus is released, hide dropdown content
  useChangeEffect(isFocus, value => {
    if (!value) setIsOpen(false);
  });

  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [isOpen]);

  return (
    <>
      {AnchorComp &&
        React.cloneElement(AnchorComp, {
          ...focusEvents,
          isOpen,
          setIsOpen: setIsOpen,
          setReference: refs.setReference,
        })}
      {ContentComp && isOpen && (
        <FloatingPortal>
          <TagSurfaceContext.Provider value={undefined}>
            {React.cloneElement(ContentComp, {
              style: { ...floatingStyles, ...contentObjectStyle },
              ...focusEvents,
              setIsOpen: setIsOpen,
              setFloating: refs.setFloating,
            })}
          </TagSurfaceContext.Provider>
        </FloatingPortal>
      )}
    </>
  );
};
